import { API_URL, getAccessToken, refreshAccessToken } from "./api";
import { adaptiveUpload } from "./adaptiveUpload";

/**
 * Inspection photo and video uploads that survive the field (orders overhaul
 * Phase 2C): switching tabs or orders, the page reloading, and weak or lost
 * marina signal.
 *
 * One queue for the whole app, not the tab. Each upload is kept in the
 * browser's IndexedDB with the file itself, so a reload (or reopening
 * Chariot later) picks it up again — AdminLayout starts the queue. It sends
 * one file at a time through adaptiveUpload (stall detection, compression on
 * a slow link). With no signal it waits and tries again — when the device is
 * back online, when the page comes back to the front, and on a backoff up to
 * every five minutes. An answer that trying again won't change (the item was
 * deleted, no permission, a file the server won't take) stops it as failed
 * until the person tries again or removes it. Two Chariot tabs share the
 * queue without sending a file twice (a Web Lock; each pass re-reads the
 * stored queue), and every try carries the upload's id, so the server keeps
 * one photo per upload even when a try got there but its answer was lost
 * (migration 213). Each upload is the person's who added it: on a shared iPad
 * the next person to sign in never sends — or sees — someone else's photos;
 * they wait for that person (setUploadQueueUser, from AdminLayout). Where
 * IndexedDB isn't available (some private windows) the queue still works for
 * as long as the page stays open.
 *
 * States: queued → uploading (compressing / retrying inside adaptiveUpload) →
 * done; waiting (between tries or offline); failed (needs the person).
 */

const DB_NAME = "chariot-uploads";
const STORE = "uploads";
const LOCK = "chariot-upload-queue";
const BACKOFF_MS = [30000, 60000, 120000, 300000];
const AUTH_TRIES = 2; // renewing the sign-in, per upload (apart from tries lost to the signal)
const PERMANENT = (status) => status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;

let entries = [];
const listeners = new Set();
const doneListeners = new Set();
let started = false;
let owner = null; // the signed-in person's id (setUploadQueueUser)
let pumping = false;
let pumpAgain = false; // asked while a pass was running: run one more
let wakeTimer = null;

// ── IndexedDB (never throws; null when unavailable) ─────────────────────────
// iOS closes the connection when Safari sits in the background, and every
// later transaction on it fails: a lost connection is let go of, and the next
// use opens a new one.
let dbPromise = null;
function openDb() {
  if (!dbPromise) {
    const opening = new Promise((resolve) => {
      try {
        if (typeof indexedDB === "undefined") { resolve(null); return; }
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
        req.onsuccess = () => {
          const db = req.result;
          const lose = () => { if (dbPromise === opening) dbPromise = null; };
          db.onclose = lose;
          db.onversionchange = () => { try { db.close(); } catch { /* fine */ } lose(); };
          resolve(db);
        };
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    dbPromise = opening;
  }
  return dbPromise;
}

// One operation: { ok, value }. A failed one is tried once more on a new
// connection (the one it used may be lost).
async function idb(mode, run, again = true) {
  const db = await openDb();
  if (!db) return { ok: false };
  const result = await new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const req = run(tx.objectStore(STORE));
      tx.oncomplete = () => resolve({ ok: true, value: req ? req.result : undefined });
      tx.onerror = () => resolve({ ok: false });
      tx.onabort = () => resolve({ ok: false });
    } catch {
      resolve({ ok: false });
    }
  });
  if (result.ok || !again) return result;
  if (dbPromise && (await dbPromise) === db) {
    try { db.close(); } catch { /* fine */ }
    dbPromise = null;
  }
  return idb(mode, run, false);
}

const stored = (e) => ({ id: e.id, path: e.path, meta: e.meta, file: e.file, createdAt: e.createdAt, attempts: e.attempts, state: e.state === "failed" ? "failed" : "queued", error: e.error || null });
const save = async (e) => { e.persisted = (await idb("readwrite", (s) => s.put(stored(e)))).ok; };
const drop = async (id) => (await idb("readwrite", (s) => s.delete(id))).ok;

// ── State and listeners ─────────────────────────────────────────────────────
// The signed-in person's uploads (ones kept before uploads had an owner go
// with whoever is signed in).
const mine = (e) => !e.meta?.userId || e.meta.userId === owner;
const snapshot = () => entries.filter(mine).map((e) => ({ ...e }));
function notify() {
  const list = snapshot();
  listeners.forEach((fn) => { try { fn(list); } catch { /* a listener's problem */ } });
}
const withPreview = (e) => {
  if (!e.previewUrl && e.file) {
    try { e.previewUrl = URL.createObjectURL(e.file); } catch { e.previewUrl = null; }
  }
  return e;
};
function forget(id) {
  const e = entries.find((x) => x.id === id);
  if (e?.previewUrl) { try { URL.revokeObjectURL(e.previewUrl); } catch { /* fine */ } }
  entries = entries.filter((x) => x.id !== id);
  notify();
}

// Picks up uploads stored by another tab (or before a reload) and lets go of
// ones another tab finished — their media is then reloaded (result null).
async function reconcile() {
  const { ok, value: rows } = await idb("readonly", (s) => s.getAll());
  if (!ok || !Array.isArray(rows)) return;
  const ids = new Set(rows.map((r) => r.id));
  let changed = false;
  for (const r of rows) {
    if (!entries.some((e) => e.id === r.id)) {
      entries.push(withPreview({ ...r, percent: 0, compressed: false, nextAttemptAt: 0, persisted: true }));
      changed = true;
    }
  }
  for (const e of [...entries]) {
    if (e.persisted && !ids.has(e.id) && e.state !== "uploading" && e.state !== "done") {
      doneListeners.forEach((fn) => { try { fn(e, null); } catch { /* fine */ } });
      forget(e.id);
    }
  }
  if (changed) notify();
}

// ── Sending ─────────────────────────────────────────────────────────────────
function wakeAt(ms) {
  if (wakeTimer) clearTimeout(wakeTimer);
  wakeTimer = setTimeout(() => { wakeTimer = null; pump(); }, Math.max(1000, ms));
}

async function send(e) {
  e.inFlight = true; // until this try ends, whatever state its progress shows
  e.state = "uploading";
  e.percent = 0;
  e.error = null;
  e.serverTrouble = false;
  notify();
  try {
    const result = await adaptiveUpload({
      file: e.file,
      // Its id goes with every try (one photo per upload on the server).
      url: `${API_URL}${e.path}${e.path.includes("?") ? "&" : "?"}upload_id=${encodeURIComponent(e.id)}`,
      headers: { Authorization: `Bearer ${getAccessToken()}` },
      onProgress: (p) => {
        if (p.phase === "failed" || p.phase === "success") return;
        e.state = p.phase === "waiting" ? "retrying" : p.phase;
        e.percent = p.percent || 0;
        e.compressed = !!p.compressed;
        notify();
      },
    });
    e.state = "done";
    e.percent = 100;
    notify();
    // (If this fails, a reload sends it again — and the server answers with
    // the photo it already has.)
    await drop(e.id);
    doneListeners.forEach((fn) => { try { fn(e, result); } catch { /* fine */ } });
    setTimeout(() => forget(e.id), 1500);
  } catch (err) {
    if (e.discarded) { // its item was deleted while this try was out
      await drop(e.id);
      forget(e.id);
      return;
    }
    const status = err?.status || Number((/HTTP (\d{3})/.exec(err?.message || "") || [])[1]) || 0;
    if (status === 401 && (e.authTries || 0) < AUTH_TRIES) {
      // The sign-in ran out: renew it and go again — counted on its own, so
      // tries lost to weak signal before don't use these up.
      e.authTries = (e.authTries || 0) + 1;
      try { await refreshAccessToken(); } catch { /* signed out: waits for the next sign-in */ }
      e.state = "waiting";
      e.nextAttemptAt = Date.now() + 2000;
    } else if (PERMANENT(status) || status === 401) {
      e.state = "failed";
      e.error = status === 404 ? "The item it was for is gone."
        : status === 403 ? "Not allowed."
          : status === 401 ? "Sign in again, then retry."
            : (err?.message || "The server wouldn't take it.");
    } else {
      e.attempts = (e.attempts || 0) + 1;
      e.state = "waiting";
      e.nextAttemptAt = Date.now() + BACKOFF_MS[Math.min(e.attempts - 1, BACKOFF_MS.length - 1)];
      // No answer at all is the signal; an error answer (5xx, busy, timed
      // out) is Chariot's side. Both try again by themselves.
      e.serverTrouble = status >= 500 || status === 408 || status === 429;
      e.error = e.serverTrouble ? "Chariot couldn't store it yet" : "Waiting for signal";
    }
    notify();
    await save(e);
  } finally {
    e.inFlight = false;
  }
}

async function drain() {
  for (;;) {
    await reconcile();
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      // No signal: say so (the "online" event resumes it).
      let changed = false;
      for (const e of entries) {
        if (e.state === "queued") { e.state = "waiting"; e.error = "Waiting for signal"; e.serverTrouble = false; changed = true; }
      }
      if (changed) notify();
      return;
    }
    const now = Date.now();
    const ready = entries.filter((e) => (e.state === "queued" || e.state === "waiting") && mine(e));
    const next = ready.find((e) => (e.nextAttemptAt || 0) <= now);
    if (!next) {
      const soonest = Math.min(...ready.map((e) => e.nextAttemptAt || now));
      if (Number.isFinite(soonest)) wakeAt(soonest - now);
      return;
    }
    if (!getAccessToken()) { wakeAt(5000); return; } // not signed in yet (a reload)
    await send(next);
  }
}

async function pump() {
  if (!started) return;
  if (pumping) { pumpAgain = true; return; }
  pumping = true;
  pumpAgain = false;
  try {
    if (typeof navigator !== "undefined" && navigator.locks?.request) {
      await navigator.locks.request(LOCK, { ifAvailable: true }, async (lock) => {
        if (lock) await drain();
        else wakeAt(3000); // another tab is sending; look again shortly
      });
    } else {
      await drain();
    }
  } catch (err) {
    console.error("Upload queue:", err);
    wakeAt(30000);
  } finally {
    pumping = false;
    if (pumpAgain) pump();
  }
}

// ── Public ──────────────────────────────────────────────────────────────────

/** Starts the queue once per page (AdminLayout): resumes stored uploads. */
export function startUploadQueue() {
  if (started) return;
  started = true;
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => pump());
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") pump(); });
  }
  pump();
}

/**
 * The signed-in person (AdminLayout, on every sign-in and sign-out): their
 * uploads are the ones sent and listed.
 */
export function setUploadQueueUser(userId) {
  const next = userId ? String(userId) : null;
  if (next === owner) return;
  owner = next;
  notify();
  pump();
}

/**
 * Adds a file to send to `path` (under the API, e.g.
 * /inspections/:id/items/:itemId/media). `meta` travels with it — what the
 * page needs to show it (inspectionId, itemId, fileName…). Returns its id.
 */
export function enqueueUpload({ path, file, meta = {} }) {
  const id = `up-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const e = withPreview({ id, path, file, meta: { fileName: file?.name, mimeType: file?.type, ...meta, userId: owner }, createdAt: Date.now(), attempts: 0, state: "queued", percent: 0, compressed: false, nextAttemptAt: 0, persisted: false });
  entries.push(e);
  notify();
  startUploadQueue();
  save(e).then(() => pump());
  return id;
}

/** Tries a failed or waiting upload again now. */
export function retryUpload(id) {
  const e = entries.find((x) => x.id === id);
  if (!e || e.inFlight || e.state === "done") return;
  e.state = "queued";
  e.nextAttemptAt = 0;
  e.attempts = 0; // a fresh start: the next lost try waits 30 s, not five minutes
  e.authTries = 0;
  e.error = null;
  notify();
  save(e).then(() => pump());
}

/** Removes an upload that hasn't been sent (asked first by the page). */
export async function discardUpload(id) {
  const e = entries.find((x) => x.id === id);
  if (!e || e.inFlight || e.state === "done") return;
  await drop(id);
  forget(id);
}

/**
 * Removes the uploads `pick(entry)` chooses — the ones for an item or an
 * inspection just deleted, which could never arrive. One on its way now is
 * let go of when that try ends.
 */
export async function discardUploads(pick) {
  for (const e of entries.filter(pick)) {
    if (e.state === "done") continue;
    if (e.inFlight) { e.discarded = true; continue; }
    await drop(e.id);
    forget(e.id);
  }
}

/** Calls fn(list) now and on every change; returns the unsubscribe. */
export function subscribeUploads(fn) {
  listeners.add(fn);
  fn(snapshot());
  return () => listeners.delete(fn);
}

/** Calls fn(entry, result) when an upload is done (result null: done elsewhere). */
export function onUploadDone(fn) {
  doneListeners.add(fn);
  return () => doneListeners.delete(fn);
}
